import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  getAssignmentDiscussionDetails,
  type AssignmentDiscussionDetails,
} from "../../api/assignment";

type AssignmentDiscussionContextValue = {
  leadId: string | null;
  details: AssignmentDiscussionDetails | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
};

const AssignmentDiscussionContext =
  createContext<AssignmentDiscussionContextValue | null>(null);

type ProviderProps = {
  leadId: string | null | undefined;
  children: ReactNode;
};

export function AssignmentDiscussionProvider({ leadId, children }: ProviderProps) {
  const normalizedLeadId = leadId ?? null;
  const [details, setDetails] = useState<AssignmentDiscussionDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(!!normalizedLeadId);
  const [error, setError] = useState<Error | null>(null);
  const requestSeqRef = useRef(0);

  const fetchDetails = useCallback(
    async (targetLeadId: string) => {
      const seq = ++requestSeqRef.current;
      setLoading(true);
      try {
        const data = await getAssignmentDiscussionDetails(targetLeadId);
        if (seq !== requestSeqRef.current) return;
        setDetails(data);
        setError(null);
      } catch (err) {
        if (seq !== requestSeqRef.current) return;
        setError(err instanceof Error ? err : new Error("Failed to load details"));
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!normalizedLeadId) {
      requestSeqRef.current += 1;
      setDetails(null);
      setError(null);
      setLoading(false);
      return;
    }
    fetchDetails(normalizedLeadId);
  }, [normalizedLeadId, fetchDetails]);

  const refresh = useCallback(async () => {
    if (!normalizedLeadId) return;
    await fetchDetails(normalizedLeadId);
  }, [normalizedLeadId, fetchDetails]);

  return (
    <AssignmentDiscussionContext.Provider
      value={{ leadId: normalizedLeadId, details, loading, error, refresh }}
    >
      {children}
    </AssignmentDiscussionContext.Provider>
  );
}

export function useAssignmentDiscussion(): AssignmentDiscussionContextValue {
  const ctx = useContext(AssignmentDiscussionContext);
  if (!ctx) {
    throw new Error(
      "useAssignmentDiscussion must be used inside <AssignmentDiscussionProvider>"
    );
  }
  return ctx;
}
